import React, { useMemo } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import i18n from '../../utilities/i18n';
import {
  Button,
  Card,
  Highlight,
  HStack,
  IconBannersWarning,
  Text,
  VStack,
} from '@adc/parallax-component-library';
import { generateYupPasswordSchema } from '../../services/auth';
import { useAppSelector } from '../../reducers/hooks';
import PasswordInputWithError from '../ui/forms/PasswordInputWithError';
import { ConfirmationModal } from '../modal/ConfirmationModal';
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import { ROUTES } from '../../constants';

export type UpdatePasswordFormValues = {
  currentPassword?: string;
  password: string;
  confirmPassword: string;
};

type UpdatePasswordProps = {
  title: string;
  description?: string;
  showCurrentPassword?: boolean;
  onSubmit: (
    values: UpdatePasswordFormValues,
    helpers: FormikHelpers<UpdatePasswordFormValues>,
  ) => void;
  errorMessage: string;
  successModalOpen: boolean;
};

/**
 * Shared form for updating the current user's password.
 * @param {string} title -
 * @param {string} description - Optional description text to display above the form.
 * @param {boolean} showCurrentPassword - Whether to show the current password field (for Change Password flow).
 * @param {function(UpdatePasswordFormValues, FormikHelpers<UpdatePasswordFormValues>): voids} onSubmit - Callback for form submission.
 * @param {errorMessage} errorMessage - Error message to display if the update fails.
 * @param {boolean} successModalOpen - Whether the success modal is open.
 * @returns {React.JSX.Element}
 */
export default function UpdatePassword({
  title,
  description,
  showCurrentPassword = false,
  onSubmit,
  errorMessage,
  successModalOpen,
}: UpdatePasswordProps) {
  const navigate = useNavigate();
  const passwordRules = useAppSelector((state) => state.config.passwordRules);

  let validationSchema = generateYupPasswordSchema(passwordRules);

  if (showCurrentPassword) {
    validationSchema = validationSchema.concat(
      Yup.object().shape({
        currentPassword: Yup.string().required(i18n.t('Form.passwordRequired')),
      }),
    );
  }

  const passwordRulesLabels = useMemo(() => {
    return passwordRules.requirements.map((req) =>
      i18n.t(`PasswordRequirements.${req}`, {
        min: passwordRules.rules.min?.value,
        max: passwordRules.rules.max?.value,
        count: passwordRules.rules.count?.value || 1,
      }),
    );
  }, [passwordRules]);

  const initialValues: UpdatePasswordFormValues = {
    password: '',
    confirmPassword: '',
    ...(showCurrentPassword ? { currentPassword: '' } : {}),
  };

  const navigateToLogin = () => navigate(ROUTES.AUTH.LOGIN);

  return (
    <VStack
      className="align-center"
      minWidth="50%"
      maxWidth="100%"
      padding="$8"
    >
      <Card>
        <VStack margin="$4">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {(formikProps: FormikProps<UpdatePasswordFormValues>) => (
              <Form noValidate id="auth-update-password">
                <VStack gap="$4">
                  <VStack alignItems="center" gap="$2">
                    <Text type="$h2.strong">{title}</Text>

                    {description ? (
                      <Text type="$bodyBase.default">{description}</Text>
                    ) : null}
                  </VStack>

                  {errorMessage ? (
                    <VStack marginBottom={'$4'}>
                      <Highlight
                        variant="danger"
                        iconStart={
                          <IconBannersWarning
                            color="$neutral.0"
                            accentColor="$support.danger.default"
                          />
                        }
                        title={i18n.t('Auth_PasswordUpdate.errorTitle')}
                        body={errorMessage}
                        testID="update-password-error-banner"
                      />
                    </VStack>
                  ) : null}

                  {showCurrentPassword ? (
                    <PasswordInputWithError
                      name="currentPassword"
                      label={i18n.t('Auth_PasswordUpdate.currentPassword')}
                      testID="current-password-input"
                      autocomplete="current-password"
                    />
                  ) : null}

                  <PasswordInputWithError
                    name="password"
                    label={i18n.t('Form.password')}
                    testID="password-input"
                    autocomplete="new-password"
                    passwordRules={passwordRulesLabels}
                    passwordRulesTitle={i18n.t('PasswordRequirements.title')}
                  />

                  <PasswordInputWithError
                    name="confirmPassword"
                    label={i18n.t('Form.passwordConfirm')}
                    testID="confirm-password-input"
                    autocomplete="new-password"
                  />

                  <HStack justifyContent="center">
                    <Button
                      onPress={formikProps.submitForm}
                      label={i18n.t('Common.submit')}
                      disabled={
                        formikProps.isSubmitting || !formikProps.isValid
                      }
                      variant="primary"
                      testID="update-password-submit-button"
                    />
                  </HStack>
                </VStack>
              </Form>
            )}
          </Formik>
        </VStack>
      </Card>
      <ConfirmationModal
        title={i18n.t('Auth_PasswordUpdate.successTitle')}
        description={i18n.t('Auth_PasswordUpdate.successMessage')}
        isOpen={successModalOpen}
        onConfirm={navigateToLogin}
      />
    </VStack>
  );
}
